import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useApi } from '../hooks/useApi'
import { api } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from '../components/common/LoadingSpinner'
import ErrorMessage from '../components/common/ErrorMessage'
import ConfirmationModal from '../components/common/ConfirmationModal'
import SaleShowMap from '../components/common/SaleShowMap'
import SocialShareLinks from '../components/common/SocialShareLinks'
import { TrashIcon, PencilIcon, ArrowLeftIcon, MapPinIcon, CalendarIcon, UserIcon, ShieldCheckIcon, ClockIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { usePageTitle } from '../hooks/usePageTitle'
import { idFromSlug, eventPath, saleEditPath } from '../utils/slugs'

const getMapImageUrl = (location) => {
  if (!location?.latitude || !location?.longitude) return null
  const mapboxToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN
  if (!mapboxToken) return null

  const width = 800
  const height = 400
  const zoom = 14
  const markerColor = '#ef4444' // Red color for sale markers
  const marker = `pin-s+${markerColor.replace('#', '')}(${location.longitude},${location.latitude})`

  return `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/${marker}/${location.longitude},${location.latitude},${zoom},0/${width}x${height}?access_token=${mapboxToken}`
}

export default function SaleShowPage() {
  const { eventId: eventSlug, id: saleSlug } = useParams()
  const eventId = idFromSlug(eventSlug)
  const id = idFromSlug(saleSlug)
  const navigate = useNavigate()
  const { data: sale, loading, error, execute: fetchSale } = useApi()
  const { data: allSales, execute: fetchAllSales } = useApi()
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const { user, isAdmin } = useAuth()

  const saleTitle = sale?.title ? `${sale.title} — Garage Sale in ${sale.location?.city}, ${sale.location?.state}` : 'Garage Sale'
  const saleDescription = sale?.description?.substring(0, 160) ||
    (sale?.location ? `Garage sale in ${sale.location.city}, ${sale.location.state}` : '')
  usePageTitle(saleTitle, {
    description: saleDescription,
    ogImage: sale?.primary_photo_url
  })

  useEffect(() => {
    if (!eventId || !id) return
    fetchSale(() => api.sales.get(eventId, id))
    fetchAllSales(() => api.events.getSales(eventId))
  }, [eventId, id])

  const handleDeleteClick = () => {
    setDeleteModalOpen(true)
  }

  const handleDeleteConfirm = async () => {
    try {
      await api.sales.delete(id)
      setDeleteModalOpen(false)
      navigate(eventPath(sale.event))
    } catch (error) {
      console.error('Error deleting sale:', error)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  if (error) {
    return (
      <ErrorMessage
        message={error.message}
        onRetry={() => fetchSale(() => api.sales.get(eventId, id))}
        className="max-w-4xl mx-auto mt-8"
      />
    )
  }

  if (!sale) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <ErrorMessage
          message="Sale not found"
          className="max-w-4xl mx-auto mt-8"
        />
      </div>
    )
  }

  const images = []
  if (sale.primary_photo_url) {
    images.push(sale.primary_photo_url)
  }
  if (sale.photo_urls && sale.photo_urls.length > 0) {
    images.push(...sale.photo_urls.filter(url => url !== sale.primary_photo_url))
  }

  const canEdit = user && sale.user && (sale.user.id === user.id || isAdmin)
  const canDelete = user && sale.user && (sale.user.id === user.id || sale.event?.user?.id === user.id || isAdmin)
  const isOwner = user && sale.user && sale.user.id === user.id
  const isPending = sale.approval_status === 'pending'
  const isRejected = sale.approval_status === 'rejected'

  // Prepare sharing data
  const currentUrl = window.location.href
  const shareTitle = `${sale.title} - Garage Sale`
  const shareDescription = `Check out this garage sale: ${sale.title} in ${sale.location?.city}, ${sale.location?.state}. Part of ${sale.event.name}.`
  const shareHashtags = ['garagesale', 'deals', 'shopping', 'community']

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
    >
      {/* Approval status banner */}
      {isOwner && isPending && (
        <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-600 p-4">
          <div className="flex items-center gap-3">
            <ClockIcon className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-amber-800 dark:text-amber-300">Pending Approval</p>
              <p className="text-sm text-amber-700 dark:text-amber-400">Your sale is waiting for the event organizer to approve it. Only you can see it right now. You can edit or delete it while it's pending.</p>
            </div>
          </div>
        </div>
      )}
      {isOwner && isRejected && (
        <div className="mb-6 rounded-xl border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-600 p-4">
          <div className="flex items-center gap-3">
            <XMarkIcon className="h-5 w-5 text-red-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-red-800 dark:text-red-300">Sale Rejected</p>
              <p className="text-sm text-red-700 dark:text-red-400">Your sale was not approved by the event organizer. You can edit and resubmit it, or delete it.</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <Link
            to={eventPath(sale.event)}
            className="inline-flex items-center text-primary-600 hover:text-primary-700
              min-h-[44px] transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to {sale.event.name}
          </Link>

          {(canEdit || canDelete) && (
            <div className="flex gap-2">
              {canEdit && (
                <Link
                  to={saleEditPath(sale.event, sale)}
                  className="btn-secondary btn-sm"
                >
                  <PencilIcon className="h-4 w-4" />
                  Edit
                </Link>
              )}
              {canDelete && (
                <button
                  onClick={handleDeleteClick}
                  className="btn-sm bg-red-50 text-red-600 border border-red-200
                    hover:bg-red-100 hover:text-red-700
                    dark:bg-red-50 dark:text-red-500 dark:border-red-200
                    dark:hover:bg-red-100 dark:hover:text-red-400
                    transition-colors"
                >
                  <TrashIcon className="h-4 w-4" />
                  Delete
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <h1 className="text-3xl font-bold text-text">{sale.title}</h1>
          {isAdmin && sale.user?.id !== user.id && (
            <span className="badge-info">
              <ShieldCheckIcon className="h-3 w-3" />
              Admin View
            </span>
          )}
        </div>

        {/* Social Share Links */}
        <div className="border-t border-border pt-4 mt-4">
          <SocialShareLinks
            url={currentUrl}
            title={shareTitle}
            description={shareDescription}
            hashtags={shareHashtags}
            className="justify-center sm:justify-start"
          />
        </div>
      </div>

      {/* Map */}
      {sale.location?.latitude && sale.location?.longitude && (
        <div className="mb-8">
          <SaleShowMap
            className="h-[300px] sm:h-[400px] rounded-xl overflow-hidden border border-border"
            saleLocation={sale.location}
            eventLocation={sale.event?.location}
            radius={sale.event?.radius}
            otherSales={allSales || []}
            currentSaleId={sale.id}
          />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Images */}
        <div>
          {images.length > 0 ? (
            <div>
              <div className="aspect-w-16 aspect-h-12 mb-4">
                <img
                  src={images[selectedImageIndex]}
                  alt={sale.title}
                  className="object-cover rounded-xl w-full h-full"
                />
              </div>

              {images.length > 1 && (
                <div className="grid grid-cols-4 gap-2">
                  {images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`aspect-w-16 aspect-h-12 rounded-lg overflow-hidden border-2
                        min-h-[44px] transition-all duration-200 ${
                        selectedImageIndex === index
                          ? 'border-primary-500 ring-2 ring-primary-500/20'
                          : 'border-border hover:border-border-strong'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`${sale.title} ${index + 1}`}
                        className="object-cover w-full h-full"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : sale.location && (
            <div className="aspect-w-16 aspect-h-12">
              <img
                src={getMapImageUrl(sale.location)}
                alt={`Map of ${sale.location.address}`}
                className="object-cover rounded-xl w-full h-full"
              />
            </div>
          )}
        </div>

        {/* Details */}
        <div className="space-y-6">
          {/* Description */}
          <div className="card p-5">
            <h2 className="text-lg font-semibold text-text mb-3">Description</h2>
            <p className="text-text-secondary whitespace-pre-wrap">{sale.description}</p>
          </div>

          {/* Location */}
          <div className="card p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-text">Location</h2>
              {sale.location?.address && (
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                    [sale.location.address, sale.location.city, sale.location.state, sale.location.zip_code]
                      .filter(Boolean)
                      .join(', ')
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 min-h-[44px]
                    text-sm font-medium text-primary-600 hover:text-primary-700
                    hover:bg-primary-50 dark:hover:bg-primary-50 rounded-lg transition-colors"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Directions
                </a>
              )}
            </div>
            <div className="flex items-start">
              <MapPinIcon className="h-5 w-5 text-text-muted mt-0.5 mr-2 flex-shrink-0" />
              <div>
                <p className="text-text-secondary">{sale.location.address}</p>
                {(sale.location.city || sale.location.state || sale.location.zip_code) && (
                  <p className="text-text-muted text-sm">
                    {[sale.location.city, sale.location.state, sale.location.zip_code]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Event Info */}
          <div className="card p-5">
            <h2 className="text-lg font-semibold text-text mb-3">Event</h2>
            <div className="space-y-2">
              <div className="flex items-center">
                <CalendarIcon className="h-5 w-5 text-text-muted mr-2" />
                <Link
                  to={eventPath(sale.event)}
                  className="text-primary-600 hover:text-primary-700 hover:underline font-medium
                    transition-colors"
                >
                  {sale.event.name}
                </Link>
              </div>
              {(sale.event.start_date || sale.event.end_date) && (
                <div className="ml-7 text-text-secondary text-sm">
                  {sale.event.start_date && sale.event.end_date ? (
                    <>
                      {new Date(sale.event.start_date).toLocaleDateString() === new Date(sale.event.end_date).toLocaleDateString() ? (
                        <p>
                          {new Date(sale.event.start_date).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      ) : (
                        <p>
                          {new Date(sale.event.start_date).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric'
                          })} - {new Date(sale.event.end_date).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                      )}
                    </>
                  ) : sale.event.start_date ? (
                    <p>
                      Starts: {new Date(sale.event.start_date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  ) : (
                    <p>
                      Ends: {new Date(sale.event.end_date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Seller Info */}
          <div className="card p-5">
            <h2 className="text-lg font-semibold text-text mb-3">Seller</h2>
            <div className="flex items-center">
              <UserIcon className="h-5 w-5 text-text-muted mr-2" />
              <span className="text-text-secondary">{sale.user?.name || 'Anonymous'}</span>
              {isAdmin && sale.user?.email && (
                <span className="ml-2 text-sm text-text-muted">({sale.user.email})</span>
              )}
            </div>
          </div>

          {/* Posted Date */}
          <div className="card p-5">
            <h2 className="text-lg font-semibold text-text mb-3">Posted</h2>
            <p className="text-text-secondary">
              {new Date(sale.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Sale"
        message="Are you sure you want to delete this sale? This action cannot be undone."
      />
    </motion.div>
  )
}
